// Argus — EntryIcon: render iconiță reală Windows (extras de agent) cu fallback MUI generic.
// Pe load error → arată MUI Folder/InsertDriveFile (browser cache HTTP funcționează automat
// via Cache-Control max-age=86400 + ETag pe endpoint /api/v1/agents/{id}/icons/{hash}).
//
// Pentru picture/video files (post frontend-mui-fixes-iter2): dacă isVisible=true (intrat în
// viewport Virtuoso) și parentPath + entry.modifiedAt sunt prezente, înscriere în
// ThumbnailQueue (worker serial 1-at-a-time peste viewport hashes). Cleanup la unsubscribe.

import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { iconUrl, joinPath, type FsEntry } from '../../lib/fsEntries';
import { iconCache } from '../../lib/iconCache';
import { ThumbnailQueue, computeFileHash, fileThumbKindFromName, thumbnailUrl } from '../../lib/fileThumbnails';

export interface EntryIconProps {
  entry: FsEntry;
  agentId: string;
  size?: number;
  /** Path-ul folderului curent (necesar pentru a calcula absPath = joinPath(parentPath, entry.name)). */
  parentPath?: string | undefined;
  /** Set true de DirectoryContent ThumbnailsView când entry intră în Virtuoso visible viewport. */
  isVisible?: boolean | undefined;
  /** Activează lazy load thumbnail (default true în ThumbnailsView, false în Details/List). */
  enableThumbnail?: boolean | undefined;
}

interface IconRenderState {
  readonly key: string;
  readonly thumbUrl: string | null;
  readonly thumbErrored: boolean;
  readonly iconErrored: boolean;
}

function makeKey(parentPath: string | undefined, entry: FsEntry): string {
  return `${parentPath ?? ''}|${entry.name}|${entry.modifiedAt ?? ''}`;
}

export function EntryIcon({ entry, agentId, size = 24, parentPath, isVisible = false, enableThumbnail = false }: EntryIconProps): JSX.Element {
  // State cu key derivat din entry — Virtuoso reuse-ează DOM nodes la scroll/back, deci
  // state-ul (thumbUrl blob URL) poate persista cross-entries. Verificăm key match → invalidare
  // automată dacă entry e diferit decât cel pentru care state-ul a fost setat.
  const entryKey = makeKey(parentPath, entry);
  const [renderState, setRenderState] = useState<IconRenderState>(() => ({
    key: entryKey, thumbUrl: null, thumbErrored: false, iconErrored: false,
  }));
  const isStale = renderState.key !== entryKey;
  const thumbUrl = isStale ? null : renderState.thumbUrl;
  const thumbErrored = isStale ? false : renderState.thumbErrored;
  const iconErrored = isStale ? false : renderState.iconErrored;

  const thumbKind = (!entry.isDir && enableThumbnail) ? fileThumbKindFromName(entry.name) : null;

  useEffect(() => {
    // Reset state la schimbarea entry — sync cu key match.
    setRenderState({ key: entryKey, thumbUrl: null, thumbErrored: false, iconErrored: false });

    if (!enableThumbnail || !isVisible || thumbKind === null || parentPath === undefined) {
      return;
    }
    const modAtRaw = entry.modifiedAt;
    if (modAtRaw === null || modAtRaw === undefined) return;
    const mtimeUnix = Math.floor(new Date(modAtRaw).getTime() / 1000);
    if (!Number.isFinite(mtimeUnix) || mtimeUnix <= 0) return;
    const absPath = joinPath(parentPath, entry.name);

    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    void (async () => {
      const fileHash = await computeFileHash(absPath, mtimeUnix);
      if (cancelled) return;
      unsubscribe = ThumbnailQueue.subscribe(
        { fileHash, agentId, absPath, mtime: mtimeUnix, extKind: thumbKind },
        (url) => {
          if (cancelled) return;
          if (url === null) {
            setRenderState((prev) => prev.key === entryKey ? { ...prev, thumbErrored: true } : prev);
          } else {
            setRenderState((prev) => prev.key === entryKey ? { ...prev, thumbUrl: url } : prev);
          }
        },
      );
    })();
    return () => {
      cancelled = true;
      if (unsubscribe !== null) unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryKey, agentId, enableThumbnail, isVisible, thumbKind]);

  const setIconErrored = (): void => {
    setRenderState((prev) => prev.key === entryKey ? { ...prev, iconErrored: true } : prev);
  };
  const setThumbErrored = (): void => {
    setRenderState((prev) => prev.key === entryKey ? { ...prev, thumbErrored: true } : prev);
  };

  if (thumbUrl !== null && !thumbErrored) {
    // crossOrigin omis intenționat — thumbUrl e data URI (data:image/png;base64,...) post bloc
    // ws-direct-thumbnails 2026-05-09. Atributul `crossOrigin="use-credentials"` pe data URIs
    // poate cauza Chrome să respingă silențios (CORS check pentru same-origin nesensibil).
    return (
      <Box
        component="img"
        src={thumbUrl}
        alt=""
        loading="lazy"
        onError={setThumbErrored}
        sx={{ width: size, height: size, objectFit: 'contain', display: 'block', borderRadius: 0.25 }}
      />
    );
  }

  // Bloc ws-icons-inline 2026-05-05: preferă iconițele din WS payload cache memory
  // (data URLs decoded o singură dată), fallback la REST endpoint /icons/{hash}?size=...
  // pentru offline scenarios sau cache miss.
  if (entry.iconHash !== null && !iconErrored) {
    const bundle = iconCache.getBundle(entry.iconHash);
    let src: string;
    if (bundle) {
      // Pick size aliniat cu render size: <=24 → url20, <=64 → url48, else url96.
      src = size <= 24 ? bundle.url20 : size <= 64 ? bundle.url48 : bundle.url96;
    } else {
      // Fallback REST (offline sau cache miss).
      const restSize = size <= 24 ? 20 : size <= 64 ? 48 : 96;
      src = `${iconUrl(agentId, entry.iconHash)}?size=${restSize}`;
    }
    return (
      <Box
        component="img"
        src={src}
        alt=""
        loading="lazy"
        onError={setIconErrored}
        sx={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
      />
    );
  }
  if (entry.isDir) {
    return <FolderIcon sx={{ fontSize: size, color: 'primary.main' }} />;
  }
  return <InsertDriveFileIcon sx={{ fontSize: size, color: 'text.secondary' }} />;
}

export { thumbnailUrl };
